import { BASE_URL } from "@/configs";

// Lightweight i18n core. Translation dictionaries are plain key→value JSON
// files committed under `apps/main/backend/locales/admin/{lang}.json` and exposed by
// the backend at `/locales/admin/...`. The active dictionary is kept in module
// state so non-component code (utils, services, validation) can translate via
// `translate()` while React components subscribe through the
// I18nProvider/useTranslation hook.

export type Dict = Record<string, string>;
export type TVars = Record<string, string | number>;

// Shared with the LanguageSelector component so both read/write the same slot.
export const STORAGE_KEY = "sky-admin-language";
export const DEFAULT_LANG = "en";

// Languages we currently ship translation files for.
export const SUPPORTED_LANGS = ["en", "es", "it", "pt"] as const;

let currentLang: string = DEFAULT_LANG;
let currentDict: Dict = {};
const dictCache = new Map<string, Dict>();
const listeners = new Set<() => void>();

export const getStoredLang = (): string => {
  if (typeof window === "undefined") return DEFAULT_LANG;

  return window.localStorage.getItem(STORAGE_KEY) || DEFAULT_LANG;
};

// Edits made in Localization > Translations; the panel still works on the shipped text when this fails.
const loadOverrides = async (lang: string): Promise<Dict> => {
  try {
    const res = await fetch(
      `${BASE_URL}/api-admin/translation/public?app=admin&lang=${encodeURIComponent(lang)}`,
      { headers: { Accept: "application/json" } },
    );

    return res.ok ? ((await res.json()) as Dict) : {};
  } catch {
    return {};
  }
};

// Fetch (and cache) a language file. Dictionaries are immutable per session,
// so a successful fetch is cached and reused across language switches.
export const loadDict = async (lang: string): Promise<Dict> => {
  const cached = dictCache.get(lang);
  if (cached) return cached;

  const res = await fetch(`${BASE_URL}/locales/admin/${lang}.json`, {
    headers: { Accept: "application/json" },
  });
  if (!res.ok) {
    throw new Error(`Failed to load language file "${lang}" (${res.status})`);
  }
  const dict = { ...((await res.json()) as Dict), ...(await loadOverrides(lang)) };
  dictCache.set(lang, dict);

  return dict;
};

// Promote a loaded dictionary to "active" and notify subscribers so React
// consumers re-render with the new strings.
export const setActiveDict = (lang: string, dict: Dict): void => {
  currentLang = lang;
  currentDict = dict;
  listeners.forEach((listener) => listener());
};

export const getActiveLang = (): string => currentLang;

// Replace `{name}` placeholders with the matching var; leaves unknown
// placeholders untouched so missing data is visible rather than silently blank.
const interpolate = (template: string, vars?: TVars): string => {
  if (!vars) return template;

  return template.replace(/\{(\w+)\}/g, (match, key: string) =>
    key in vars ? String(vars[key]) : match,
  );
};

// Translate a key. Falls back to the key itself when the active dictionary is
// missing it, which keeps the UI readable and makes gaps easy to spot.
export const translate = (key: string, vars?: TVars): string => {
  const template = currentDict[key] ?? key;

  return interpolate(template, vars);
};

export const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
};
